// ═══ 검차 셸 포맷·그룹 유틸 (신고 371) — 화면 에이전트 공용 계약 ═══
// 앱 정본: simple_summary.dart(groupOf·simpleActiveOrders·제목 폴백) · rd_listing_bits.dart.
// 숫자·날짜·차량번호는 RTL 에서도 LTR 이어야 한다 — 표시 격리는 parts/LtrText.js 가 담당한다.

/// RTL 문장에 끼는 치환값(금액·날짜·건수) bidi 격리 — LRI(U+2066) … PDI(U+2069).
/// 문자열 안에서 순서가 뒤집히는 것을 막는다. 엘리먼트로 감쌀 수 있으면 parts/LtrText.js 를 쓴다.
export function isolate(str) {
  return '\u2066' + String(str == null ? '' : str) + '\u2069';
}

/// ARB 값 끝의 화살표 글리프('›' 등)를 뗀다 — 문자열 안에 있으면 RTL 에서 미러링되지 않는다.
/// 호출자는 대신 <span className="ss-flipx">›</span> 을 붙이거나 자기 chevron SVG 를 쓴다.
export function stripTrailingChevron(str) {
  return String(str == null ? '' : str).replace(/\s*[›»‹«]\s*$/, '');
}

/// 원 → '₩1,234,000'. 0/빈값/NaN 은 '₩0' (표 빈 셀 규칙이 아니라 금액 문장이다).
export function fmtKRW(won) {
  var v = Number(won);
  if (!v || isNaN(v)) v = 0;
  return '₩' + Math.round(v).toLocaleString('en-US');
}

/// ISO/날짜 문자열 → 'YYYY-MM-DD' (로케일 무관 LTR 고정 — 앱과 같은 표기).
export function fmtDate(locale, iso) {
  if (!iso) return '';
  var s = String(iso);
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? m[1] + '-' + m[2] + '-' + m[3] : s;
}

// 월 이름 — Intl 은 en/ar 품질이 나빠 커스텀(BUILD_PLAN §6).
var MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
  ru: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']
};

/// 'YYYY-MM' → 로케일별 월 라벨. ko 는 '2026년 9월', 그 외는 '<월> 2026'.
export function monthLabel(locale, ym) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return String(ym || '');
  var y = m[1], mi = parseInt(m[2], 10) - 1;
  if (mi < 0 || mi > 11) return String(ym);
  if (locale === 'ko') return y + '년 ' + (mi + 1) + '월';
  var names = MONTHS[locale] || MONTHS.en;
  return names[mi] + ' ' + y;
}

/// 'YYYY-MM' 에서 delta 개월 이동.
export function shiftMonth(ym, delta) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return String(ym || '');
  var total = parseInt(m[1], 10) * 12 + (parseInt(m[2], 10) - 1) + delta;
  var y = Math.floor(total / 12), mi = total - y * 12;
  return y + '-' + (mi + 1 < 10 ? '0' : '') + (mi + 1);
}

/// 오늘의 'YYYY-MM'.
export function currentMonth() {
  var d = new Date();
  var mm = d.getMonth() + 1;
  return d.getFullYear() + '-' + (mm < 10 ? '0' : '') + mm;
}

/// 상태 → 3그룹. open/taken/inspecting=inProgress · completed=actionNeeded · 그 외=finished.
export function groupOf(status) {
  if (status === 'open' || status === 'taken' || status === 'inspecting') return 'inProgress';
  if (status === 'completed') return 'actionNeeded';
  return 'finished';
}

function newestFirst(a, b) {
  var da = String((a && a.created_at) || ''), db = String((b && b.created_at) || '');
  if (da !== db) return da < db ? 1 : -1;
  return Number((b && b.id) || 0) - Number((a && a.id) || 0);
}

/// 최근 취소 건을 홈 진행 목록에 남겨 두는 기간 — 앱 simple_summary.dart kCancelledKeepDays 와 같은 값.
export var CANCELLED_KEEP_DAYS = 7;

/// 재신청 안내가 붙는 취소 사유 — 정본은 서버 utils/evalCancelReasons.js 의 needsResubmit.
var NEEDS_RESUBMIT = { UNIDENTIFIABLE_VEHICLE: 1, INSPECTOR_UNAVAILABLE: 1 };

/// 취소 사유 코드가 '다시 신청' 안내 대상인가.
export function cancelNeedsResubmit(code) { return !!NEEDS_RESUBMIT[code]; }

/// 취소 건이지만 홈 진행 목록에 남길 대상인가 — cancelled_at 이 CANCELLED_KEEP_DAYS 이내.
/// 취소 시각을 모르는 옛 건은 남기지 않는다(앱과 동일). 카운트·KPI 의미는 바꾸지 않는다 — 목록 노출만.
export function keepCancelledInActive(order, now) {
  if (!order || order.status !== 'cancelled') return false;
  var at = Date.parse(order.cancelled_at || '');
  if (isNaN(at)) return false;
  var elapsed = (now == null ? Date.now() : Number(now)) - at;
  if (elapsed < 0) return true;   // 시계 오차로 미래 시각 — 최근 건 취급
  return elapsed < CANCELLED_KEEP_DAYS * 86400000;
}

/// 홈 '진행 중' 목록 — 결정 대기 → 진행 중 → 최근 취소(7일 이내) 순, 각 그룹 최신순.
export function sortActive(orders, now) {
  var list = Array.isArray(orders) ? orders : [];
  var actionNeeded = list.filter(function (o) { return groupOf(o && o.status) === 'actionNeeded'; }).sort(newestFirst);
  var inProgress = list.filter(function (o) { return groupOf(o && o.status) === 'inProgress'; }).sort(newestFirst);
  var recentCancelled = list.filter(function (o) { return keepCancelledInActive(o, now); }).sort(newestFirst);
  return actionNeeded.concat(inProgress, recentCancelled);
}

/// 매물 링크의 host ('www.' 제거). 파싱 실패 시 ''.
export function listingHost(order) {
  var url = (order && order.listing_url) || '';
  if (!url) return '';
  try { return new URL(url).hostname.replace(/^www\./i, ''); } catch (e) { return ''; }
}

function photoCount(order) {
  var p = order && order.request_photos;
  return Array.isArray(p) ? p.length : 0;
}

/// 카드 제목 — 차종이 없으면 링크/스크린샷 신청 폴백 (앱 simpleOrderTitle).
export function orderTitle(t, order) {
  var model = (order && order.car_model) || '';
  if (model) return model;
  if ((order && order.listing_url) || '') return t('inspListingTitle');
  if (photoCount(order)) return t('inspPhotoTitle');
  return '-';
}

/// 링크·스크린샷 신청의 부제(host / '스크린샷 N장'). 차종이 있으면 '' — 호출자가 기본 부제를 쓴다.
export function orderSubtitle(t, order) {
  if ((order && order.car_model) || '') return '';
  var url = (order && order.listing_url) || '';
  if (url) { var host = listingHost(order); return host || url; }
  var n = photoCount(order);
  return n ? t('inspPhotoCount', { count: n }) : '';
}
